import { CheckCircle2, Award, Wrench, AlertCircle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { SectionHeading } from "../components/SectionHeading";
import { BrandLogo, IRVE_BRANDS } from "../components/BrandLogo";

const HERO = "https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=2000&q=85";
const QUALIFELEC_LOGO = "https://customer-assets.emergentagent.com/job_services-electro-dao/artifacts/z838tcki_logo-IRVE-L500.jpg";

const services = [
  { icon: Wrench, title: "Installation et mise en service", desc: "Installation complète de bornes de recharge pour véhicules électriques, raccordement et mise en service selon les normes en vigueur." },
  { icon: Award, title: "Mise à niveau & paramétrage", desc: "Mise à jour du matériel existant, paramétrage avancé, configuration adaptée à votre usage (résidentiel ou professionnel)." },
  { icon: AlertCircle, title: "Dépannage & entretien", desc: "Dépannage rapide, vérifications réglementaires et entretien préventif de votre infrastructure de recharge." },
];

export default function IRVE() {
  return (
    <div data-testid="page-irve">
      <section className="relative bg-[#0F172A] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-50" style={{ backgroundImage: `url(${HERO})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/85 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="flex flex-col lg:flex-row lg:items-end gap-10 justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#3DAE2B]/20 backdrop-blur-sm border border-[#3DAE2B]/50 text-white text-xs font-semibold uppercase tracking-wider mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-[#3DAE2B] animate-pulse" /> Installateur Qualifié
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight">
                Bornes de recharge pour
                <span className="block text-[#FBBF24]">véhicules électriques</span>
              </h1>
              <p className="mt-6 text-lg text-slate-200 max-w-2xl">
                Technicien IRVE Niveau 1 & 2, qualifié Qualifelec. Préférez l'excellence pour l'installation de votre borne de recharge à La Réunion.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                {["Technicien IRVE Niveau 1", "Technicien IRVE Niveau 2", "Qualifelec"].map((b) => (
                  <span key={b} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#3DAE2B]" /> {b}
                  </span>
                ))}
              </div>
            </div>
            <div className="shrink-0 hidden lg:flex items-center justify-center bg-white rounded-2xl p-6 shadow-2xl">
              <img src={QUALIFELEC_LOGO} alt="Qualifelec IRVE — Préférez l'excellence" className="h-48 w-auto" data-testid="irve-qualifelec-logo" />
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        <SectionHeading eyebrow="Notre prestation" title="Installation · Maintenance · Vérification" subtitle="Un service complet pour votre infrastructure de recharge, du conseil à l'entretien" />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {services.map((s) => (
            <div key={s.title} className="rounded-2xl border border-slate-200 bg-white p-7 hover:border-[#FBBF24]/40 hover:shadow-lg transition-all" data-testid={`irve-service-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#FBBF24] text-[#1B3A6B] mb-5">
                <s.icon className="w-6 h-6" />
              </div>
              <h3 className="font-display text-xl font-bold text-slate-900 tracking-tight">{s.title}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#1B3A6B] mb-3">Marques partenaires</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Du matériel de référence, sélectionné pour sa fiabilité</h2>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {IRVE_BRANDS.map((name) => (
              <div key={name} className="rounded-xl bg-white border border-slate-200 px-6 py-10 flex items-center justify-center hover:border-[#1B3A6B] hover:shadow-md transition-all min-h-[100px]" data-testid={`brand-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <BrandLogo name={name} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Besoin d'un expert en bornes de recharge ?</h2>
        <p className="mt-4 text-slate-600 max-w-2xl mx-auto">Particuliers ou professionnels, profitez d'un accompagnement complet : étude, installation, mise en service et maintenance sur toute l'île.</p>
        <Link to="/contact" className="inline-block mt-8">
          <Button size="lg" className="bg-[#FBBF24] hover:bg-[#F59E0B] text-[#0F172A] font-semibold rounded-full px-8 h-12" data-testid="irve-cta">
            Étudier mon projet IRVE <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </section>
    </div>
  );
}
